import React, { useState, useEffect, useContext } from 'react'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import InputGroup from 'react-bootstrap/InputGroup'
import {
  useCreateScheduledEmail,
  useCancelScheduledEmail,
  useUpdateScheduledEmail
} from 'api/hooks/use-scheduled-emails'
import { useQueryClient } from 'react-query'
import moment from 'moment'
import useMunicipalityUsers from 'api/hooks/use-municipality-users'
import { isEmpty } from 'lodash'
import UserContext from 'context/user-context'

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const SHORT_DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
const ORDINALS = ['first', 'second', 'third', 'fourth', 'fifth']

const getWeeklyLabel = (dateStr) => {
  if (!dateStr) return 'Weekly on [Day]'
  const d = new Date(dateStr + 'T00:00:00')
  return `Weekly on ${DAYS[d.getDay()]}`
}

const getNthWeekdayLabel = (dateStr) => {
  if (!dateStr) return 'Monthly on the Nth [Day]'
  const d = new Date(dateStr + 'T00:00:00')
  const nth = Math.floor((d.getDate() - 1) / 7)
  return `Monthly on the ${ORDINALS[nth]} ${DAYS[d.getDay()]}`
}

const getAnnualLabel = (dateStr) => {
  if (!dateStr) return 'Annually on [Date]'
  const d = new Date(dateStr + 'T00:00:00')
  return `Annually on ${d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}`
}

function ScheduleEmailModal({
  show,
  handleClose,
  dashboard,
  emails
}) {
  const queryClient = useQueryClient()
  const { data: users = [], isSuccess: usersSuccess } = useMunicipalityUsers({})
  const { municipality } = useContext(UserContext)
  const [view, setView] = useState('list')
  const [selectedEmail, setSelectedEmail] = useState(null)
  const [subject, setSubject] = useState(dashboard?.name || '')
  const [recipients, setRecipients] = useState([])
  const [frequency, setFrequency] = useState('once')
  const [repeatInterval, setRepeatInterval] = useState('')
  const [sendOn, setSendOn] = useState('')
  const [emailId, setEmailId] = useState(null)
  const isEdit = !!emailId

  // Custom recurrence state
  const [repeatUnit, setRepeatUnit] = useState('week')
  const [repeatOnDays, setRepeatOnDays] = useState([])
  const [endsCondition, setEndsCondition] = useState('never')
  const [endsOnDate, setEndsOnDate] = useState('')
  const [endsAfterOccurrences, setEndsAfterOccurrences] = useState('')
  const [showCustom, setShowCustom] = useState(false)
  const [exportFormat, setExportFormat] = useState('csv')

  const resetForm = () => {
    setSelectedEmail(null)
    setSubject(dashboard?.name || '')
    setRecipients([])
    setFrequency('once')
    setRepeatInterval('')
    setSendOn('')
    setEmailId(null)
    setRepeatUnit('week')
    setRepeatOnDays([])
    setEndsCondition('never')
    setEndsOnDate('')
    setEndsAfterOccurrences('')
    setShowCustom(false)
    setNewRecipientUserId('')
    setNewRecipientEmail('')
    setUseManualEmail(false)
    setExportFormat('csv')
    setView('list')
  }

  const recipientParams = recipients.filter(r => !isEmpty(r.user_id) || r.email).map((r) => {
    const attrs = {}
    if (r.user_id) attrs.user_id = r.user_id
    if (r.email) attrs.email = r.email
    if (r.id) attrs.id = r.id
    if (r._destroy) attrs._destroy = true
    return attrs
  })

  const invalidateQueriesAndReturn = () => {
    queryClient.invalidateQueries('dashboards')
    queryClient.invalidateQueries(['scheduledEmails', dashboard?.id])
    resetForm()
  }

  const buildParams = () => {
    const base = {
      email_subject: subject,
      scheduled_email_recipients_attributes: recipientParams,
      frequency,
      send_on: sendOn,
      repeat_interval: (frequency === 'repeated' || frequency === 'custom') ? repeatInterval : null,
      export_format: exportFormat
    }

    if (frequency === 'custom') {
      base.repeat_unit = repeatUnit
      base.repeat_on_days = repeatUnit === 'week' && repeatOnDays.length > 0
        ? repeatOnDays.join(',')
        : null
      base.ends_condition = endsCondition
      base.ends_on_date = endsCondition === 'on_date' ? endsOnDate : null
      base.ends_after_occurrences = endsCondition === 'after_occurrences' ? endsAfterOccurrences : null
    } else {
      base.repeat_unit = null
      base.repeat_on_days = null
      base.ends_condition = null
      base.ends_on_date = null
      base.ends_after_occurrences = null
    }

    return base
  }

  const { mutate: createEmail } = useCreateScheduledEmail({
    dashboardId: dashboard?.id,
    options: {
      onSuccess: () => {
       invalidateQueriesAndReturn()
      }
    }
  })

  const { mutate: updateEmail } = useUpdateScheduledEmail({
    id: emailId,
    options: {
      onSuccess: () => {
       invalidateQueriesAndReturn()
      }
    }
  })

  const { mutate: cancelEmail } = useCancelScheduledEmail({
    id: emailId,
    params: {},
    options: {
      onSuccess: () => {
       invalidateQueriesAndReturn()
      }
    }
  })

  const handleEditEmail = (email) => {
    setSelectedEmail(email)
    setView('form')
  }

  const handleAddNew = () => {
    resetForm()
    setView('form')
  }

  useEffect(() => {
    if (!selectedEmail) return

    setEmailId(selectedEmail.id)
    setSubject(selectedEmail.email_subject)
    setRecipients(
      selectedEmail.recipients.map((r) => ({ id: r.id, user_id: r.user_id || '', email: r.email || '', useManualEmail: !r.user_id && !!r.email }))
    )
    setFrequency(selectedEmail.frequency)
    setRepeatInterval(selectedEmail.repeat_interval || '')
    setSendOn(selectedEmail.send_on)
    setRepeatUnit(selectedEmail.repeat_unit || 'week')
    setRepeatOnDays(selectedEmail.repeat_on_days ? selectedEmail.repeat_on_days.split(',').map(Number) : [])
    setEndsCondition(selectedEmail.ends_condition || 'never')
    setEndsOnDate(selectedEmail.ends_on_date || '')
    setEndsAfterOccurrences(selectedEmail.ends_after_occurrences || '')
    setShowCustom(selectedEmail.frequency === 'custom')
    setExportFormat(selectedEmail.export_format || 'csv')
  }, [selectedEmail])

  const handleFrequencyChange = (value) => {
    if (value === 'custom') {
      setShowCustom(true)
      setFrequency('custom')
      if (!repeatInterval) setRepeatInterval('1')
    } else {
      setShowCustom(false)
      setFrequency(value)
    }
  }

  const toggleDay = (dayIndex) => {
    setRepeatOnDays((prev) =>
      prev.includes(dayIndex) ? prev.filter((d) => d !== dayIndex) : [...prev, dayIndex].sort()
    )
  }

  // Single add-recipient form state
  const [newRecipientUserId, setNewRecipientUserId] = useState('')
  const [newRecipientEmail, setNewRecipientEmail] = useState('')
  const [useManualEmail, setUseManualEmail] = useState(false)

  const handleAddRecipient = () => {
    if (useManualEmail) {
      if (!newRecipientEmail) return
      setRecipients([...recipients, { user_id: '', email: newRecipientEmail }])
      setNewRecipientEmail('')
    } else {
      if (!newRecipientUserId) return
      setRecipients([...recipients, { user_id: newRecipientUserId, email: '' }])
      setNewRecipientUserId('')
    }
  }

  const handleRemoveRecipient = (index) => {
    const updated = [...recipients]
    const recipient = updated[index]
    if (recipient.id) {
      updated[index] = { ...recipient, _destroy: true }
    } else {
      updated.splice(index, 1)
    }
    setRecipients(updated)
  }

  const recipientUserIds = recipients.filter(r => !r._destroy).map(r => r.user_id)

  const emailHasValidDomain = (email) => {
    if (!email) return true

    const domain = email.split('@')[1]
    const validDomains = ['thirdline.io', municipality?.email_domain]
    return validDomains.includes(domain)
  }

  const allManualEmailsValid = recipients
    .filter((r) => !r._destroy && r.email)
    .every((r) => emailHasValidDomain(r.email))

  const isLegacyRepeated = frequency === 'repeated'
  const dateDependentDisabled = !sendOn
  const customEndsValid = frequency !== 'custom' ||
    endsCondition !== 'on_date' ||
    (endsOnDate && endsOnDate > sendOn)

  const frequencyLabel = (email) => {
    const dateStr = email.send_on
    switch (email.frequency) {
      case 'once': return 'Once'
      case 'daily': return 'Daily'
      case 'weekly': return getWeeklyLabel(dateStr)
      case 'monthly': return getNthWeekdayLabel(dateStr)
      case 'annually': return getAnnualLabel(dateStr)
      case 'weekdays': return 'Every weekday (Mon–Fri)'
      case 'custom': return customFrequencyLabel(email)
      case 'repeated': return `Every ${email.repeat_interval} day(s)`
      default: return email.frequency
    }
  }

  const customFrequencyLabel = (email) => {
    const interval = email.repeat_interval || 1
    const unit = email.repeat_unit || 'day'
    const unitLabel = interval == 1 ? unit : `${interval} ${unit}s`
    let label = `Every ${unitLabel}`

    if (unit === 'week' && email.repeat_on_days) {
      const dayNames = email.repeat_on_days.split(',').map(Number).map(d => DAYS[d])
      label += ` on ${dayNames.join(', ')}`
    }

    return label
  }

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {view === 'list' ? 'Scheduled Emails' : (isEdit ? 'Edit Scheduled Email' : 'Schedule Email')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {view === 'list' ? (
          <>
            {emails?.data?.length > 0 ? (
              <div className="d-flex flex-column gap-2">
                {emails.data.map((email) => (
                  <div
                    key={email.id}
                    className="d-flex justify-content-between align-items-center border rounded p-2"
                  >
                    <div>
                      <strong>{email.email_subject}</strong>
                      <div className="text-muted" style={{ fontSize: '0.85em' }}>
                        Next: {moment(email.send_on).format('MM/DD/YYYY')} &middot; {frequencyLabel(email)} &middot; {email.recipients.length} {email.recipients.length === 1 ? 'recipient' : 'recipients'}
                      </div>
                      {email.frequency !== 'once' && email.occurrence_count > 0 && (
                        <div className="text-muted" style={{ fontSize: '0.85em' }}>
                          History: Sent {email.occurrence_count} {email.occurrence_count === 1 ? 'time' : 'times'} &middot; First sent on {moment(email.created_at).format('MM/DD/YYYY')}
                        </div>
                      )}
                    </div>
                    <Button variant="link" size="sm" onClick={() => handleEditEmail(email)}>
                      Edit
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted text-center mb-0">No scheduled emails yet.</p>
            )}
          </>
        ) : (
          <>
        <Form.Group className="mb-3">
          <Form.Label>Subject</Form.Label>
          <Form.Control
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <div className="d-flex align-items-center justify-content-between mb-1">
            <Form.Label className="mb-0">Recipients</Form.Label>
            <Button
              variant="link"
              size="sm"
              className="p-0"
              style={{ fontSize: '0.8em' }}
              onClick={() => { setUseManualEmail(!useManualEmail); setNewRecipientUserId(''); setNewRecipientEmail('') }}
            >
              {useManualEmail ? 'Select from users' : 'Use a non-user email instead'}
            </Button>
          </div>
          <div className="d-flex align-items-center mb-2">
            {useManualEmail ? (
              <InputGroup className="me-2">
                <Form.Control
                  type="text"
                  placeholder="name"
                  value={newRecipientEmail ? newRecipientEmail.split('@')[0] : ''}
                  onChange={(e) => setNewRecipientEmail(e.target.value ? `${e.target.value}@${municipality?.email_domain}` : '')}
                />
                <InputGroup.Text>@{municipality?.email_domain}</InputGroup.Text>
              </InputGroup>
            ) : (
              <Form.Select
                className="me-2"
                value={newRecipientUserId}
                onChange={(e) => setNewRecipientUserId(e.target.value)}
              >
                <option value="">Select User</option>
                {usersSuccess &&
                  users.map((u) => (
                    <option key={`u-${u.id}`} value={u.id} disabled={recipientUserIds.includes(u.id)}>
                      {u.first_name} {u.last_name} - {u.email}
                    </option>
                  ))}
              </Form.Select>
            )}
            <Button variant="light-primary" size="sm" onClick={handleAddRecipient}>
              Add
            </Button>
          </div>
          {recipients.filter(r => !r._destroy).length > 0 && (
            <table className="table table-sm my-3" style={{ border: '1px solid #dee2e6' }}>
              <thead>
                <tr>
                  <th>Recipients</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {recipients.map((r, idx) => (
                  !r._destroy && (
                    <tr key={r.id || idx}>
                      <td>
                        {r.user_id
                          ? (() => { const u = users.find(u => String(u.id) === String(r.user_id)); return u ? `${u.first_name} ${u.last_name} - ${u.email}` : r.user_id })()
                          : r.email}
                      </td>
                      <td className="text-end" style={{ width: '1%', whiteSpace: 'nowrap' }}>
                        <Button variant="link" size="sm" className="p-0" onClick={() => handleRemoveRecipient(idx)}>
                          Remove
                        </Button>
                      </td>
                    </tr>
                  )
                ))}
              </tbody>
            </table>
          )}
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Send On</Form.Label>
          <Form.Control
            type="date"
            value={sendOn}
            onChange={(e) => setSendOn(e.target.value)}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Frequency</Form.Label>
          <Form.Select
            value={frequency}
            onChange={(e) => handleFrequencyChange(e.target.value)}
          >
            <option value="once">Does not repeat</option>
            <option value="daily">Daily</option>
            <option value="weekly" disabled={dateDependentDisabled}>{getWeeklyLabel(sendOn)}</option>
            <option value="monthly" disabled={dateDependentDisabled}>{getNthWeekdayLabel(sendOn)}</option>
            <option value="annually" disabled={dateDependentDisabled}>{getAnnualLabel(sendOn)}</option>
            <option value="weekdays">Every weekday (Monday to Friday)</option>
            <option value="custom">Custom...</option>
            {isLegacyRepeated && <option value="repeated">Repeat (legacy)</option>}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Export Format</Form.Label>
          <Form.Select
            value={exportFormat}
            onChange={(e) => setExportFormat(e.target.value)}
          >
            <option value="csv">CSV (Data Table)</option>
            <option value="pdf">PDF (Dashboard Charts)</option>
          </Form.Select>
        </Form.Group>

        {showCustom && (
          <div className="border rounded p-3 mb-3">
            <h6 className="mb-3">Custom recurrence</h6>
            <Form.Group className="mb-3">
              <Form.Label>Repeat every</Form.Label>
              <div className="d-flex align-items-center gap-2">
                <Form.Control
                  type="number"
                  min="1"
                  style={{ width: '80px' }}
                  value={repeatInterval}
                  onChange={(e) => setRepeatInterval(e.target.value)}
                />
                <Form.Select
                  style={{ width: '120px' }}
                  value={repeatUnit}
                  onChange={(e) => setRepeatUnit(e.target.value)}
                >
                  <option value="day">day</option>
                  <option value="week">week</option>
                  <option value="month">month</option>
                  <option value="year">year</option>
                </Form.Select>
              </div>
            </Form.Group>

            {repeatUnit === 'week' && (
              <Form.Group className="mb-3">
                <Form.Label>Repeat on</Form.Label>
                <div className="d-flex gap-1">
                  {SHORT_DAYS.map((label, idx) => (
                    <Button
                      key={idx}
                      variant={repeatOnDays.includes(idx) ? 'primary' : 'light'}
                      size="sm"
                      style={{ width: '36px', height: '36px', borderRadius: '50%', padding: 0 }}
                      onClick={() => toggleDay(idx)}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
              </Form.Group>
            )}

            <Form.Group className="mb-0">
              <Form.Label>Ends</Form.Label>
              <div className="d-flex flex-column gap-2">
                <Form.Check
                  type="radio"
                  name="endsCondition"
                  label="Never"
                  checked={endsCondition === 'never'}
                  onChange={() => setEndsCondition('never')}
                />
                <div className="d-flex align-items-center gap-2">
                  <Form.Check
                    type="radio"
                    name="endsCondition"
                    label="On"
                    checked={endsCondition === 'on_date'}
                    onChange={() => setEndsCondition('on_date')}
                  />
                  <Form.Control
                    type="date"
                    size="sm"
                    style={{ width: '180px' }}
                    value={endsOnDate}
                    onChange={(e) => setEndsOnDate(e.target.value)}
                    disabled={endsCondition !== 'on_date'}
                  />
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Form.Check
                    type="radio"
                    name="endsCondition"
                    label="After"
                    checked={endsCondition === 'after_occurrences'}
                    onChange={() => setEndsCondition('after_occurrences')}
                  />
                  <Form.Control
                    type="number"
                    size="sm"
                    min="1"
                    style={{ width: '80px' }}
                    value={endsAfterOccurrences}
                    onChange={(e) => setEndsAfterOccurrences(e.target.value)}
                    disabled={endsCondition !== 'after_occurrences'}
                  />
                  <span className="text-muted">occurrences</span>
                </div>
              </div>
            </Form.Group>
          </div>
        )}

        {isLegacyRepeated && (
          <Form.Group className="mb-3">
            <Form.Label>Repeat Interval (days)</Form.Label>
            <Form.Control
              type="number"
              value={repeatInterval}
              onChange={(e) => setRepeatInterval(e.target.value)}
            />
          </Form.Group>
        )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {view === 'list' ? (
          <>
            <Button variant="light" onClick={handleClose}>
              Close
            </Button>
            <Button variant="primary" onClick={handleAddNew}>
              Add Scheduled Email
            </Button>
          </>
        ) : (
          <>
            {isEdit && (
              <Button variant="danger" onClick={() => {
                const output = window.confirm('Are you sure you want to delete this scheduled email? This cannot be undone.')
                if (output) {
                  cancelEmail()
                }
              }}>
                Delete
              </Button>
            )}
            <Button variant="light" onClick={resetForm}>
              Back
            </Button>
            <Button
              variant="primary"
              onClick={() => (isEdit ? updateEmail(buildParams()) : createEmail(buildParams()))}
              disabled={
                !dashboard?.id ||
                !subject ||
                !recipients.some((r) => !r._destroy && (r.user_id || r.email)) ||
                !sendOn ||
                !allManualEmailsValid ||
                !customEndsValid
              }
            >
              {isEdit ? 'Save' : 'Schedule'}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  )
}

export default ScheduleEmailModal
